import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  Link as LinkIcon,
  Help as HelpIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

function MaventaLinkExistingDialog({ open, onClose, onSuccess, t }) {
  const { currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [companyUuid, setCompanyUuid] = useState("");
  const [userApiKey, setUserApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const handleLink = async () => {
    if (!companyUuid.trim()) {
      setError(t("maventa.companyUuidRequired", "Company UUID on pakollinen"));
      return;
    }
    if (!userApiKey.trim()) {
      setError(t("maventa.apiKeyRequired", "API-avain on pakollinen"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/maventa/link-existing/`,
        {
          company_uuid: companyUuid.trim(),
          user_api_key: userApiKey.trim(),
        }
      );

      if (response.data.success) {
        onSuccess(response.data);
        handleClose();
      } else {
        setError(response.data.error || t("maventa.linkFailed", "Yhdistäminen epäonnistui"));
      }
    } catch (err) {
      console.error("Failed to link Maventa account:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        t("maventa.linkFailed", "Maventa-tilin yhdistäminen epäonnistui. Tarkista tunnukset ja yritä uudelleen.");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setCompanyUuid("");
      setUserApiKey("");
      setShowHelp(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinkIcon color="primary" />
          {t("maventa.linkExistingTitle", "Yhdistä olemassa oleva Maventa-tili")}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t(
              "maventa.linkExistingInfo",
              "Jos yrityksesi on jo rekisteröity Maventaan (esim. toisen järjestelmän kautta), voit yhdistää sen Verstaaniin syöttämällä olemassa olevat tunnukset."
            )}
          </Typography>
        </Alert>

        {/* Hidden fields to prevent password manager autofill */}
        <input type="text" name="prevent-autofill-1" style={{ display: "none" }} />
        <input type="password" name="prevent-autofill-2" style={{ display: "none" }} />

        {/* Company UUID Input */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
        >
          {t("maventa.companyUuid", "Company UUID")}
        </Typography>
        <TextField
          fullWidth
          value={companyUuid}
          onChange={(e) => setCompanyUuid(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          disabled={loading}
          autoComplete="off"
          name="maventa-company-uuid"
          sx={{ mb: 3, fontFamily: "monospace" }}
          inputProps={{
            style: { fontFamily: "monospace" },
            autoComplete: "off",
            "data-form-type": "other",
          }}
        />

        {/* API Key Input */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}
        >
          {t("maventa.userApiKey", "API-avain (User API Key)")}
        </Typography>
        <TextField
          fullWidth
          type="text"
          value={userApiKey}
          onChange={(e) => setUserApiKey(e.target.value)}
          placeholder={t("maventa.apiKeyPlaceholder", "Syötä API-avain")}
          disabled={loading}
          autoComplete="off"
          name="maventa-api-key"
          sx={{ mb: 3, fontFamily: "monospace" }}
          inputProps={{
            autoComplete: "off",
            "data-form-type": "other",
            style: { fontFamily: "monospace" },
          }}
        />

        {/* Help Section */}
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<HelpIcon />}
            onClick={() => setShowHelp(!showHelp)}
            sx={{ textTransform: "none" }}
          >
            {t("maventa.whereToFindCredentials", "Mistä löydän tunnukset?")}
          </Button>

          <Collapse in={showHelp}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t(
                  "maventa.credentialsHelp",
                  "Maventa-tunnukset löytyvät yleensä aiemmasta järjestelmästäsi (esim. Procountor, Netvisor, Visma) tai Maventan omasta hallintapaneelista. Jos et löydä tunnuksia:"
                )}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    1
                  </Box>
                  <Typography variant="body2">
                    {t(
                      "maventa.helpStep1",
                      "Tarkista edellisen ohjelmiston asetukset tai hallintapaneeli"
                    )}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    2
                  </Box>
                  <Typography variant="body2">
                    {t(
                      "maventa.helpStep2",
                      "Ota yhteyttä edellisen ohjelmiston tukeen"
                    )}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    3
                  </Box>
                  <Typography variant="body2">
                    {t(
                      "maventa.helpStep3",
                      "Ota yhteyttä tukeemme: asiakaspalvelu@verstaani.fi"
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Contact Support Option */}
        <Box
          sx={{
            p: 2,
            bgcolor: "warning.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.200",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 1 }}
          >
            <EmailIcon fontSize="small" color="warning" />
            {t("maventa.cantFindCredentials", "Etkö löydä tunnuksia?")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t(
              "maventa.contactSupportInfo",
              "Jos et löydä Maventa-tunnuksiasi tai tarvitset apua tilin siirtämisessä, ota yhteyttä tukeemme:"
            )}
          </Typography>
          <Link
            href="mailto:asiakaspalvelu@verstaani.fi"
            sx={{ fontWeight: 500 }}
          >
            asiakaspalvelu@verstaani.fi
          </Link>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel", "Peruuta")}
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={loading || !companyUuid.trim() || !userApiKey.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <LinkIcon />}
        >
          {loading ? <span>{t("maventa.linking", "Yhdistetään...")}</span> : <span>{t("maventa.linkAccount", "Yhdistä tili")}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(MaventaLinkExistingDialog);
